package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Int
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.Map
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(Comment.Companion.Serializer::class)
public class Comment : Resource, CommentSchema {
  override val `companion`: Resource.CompanionObj<Comment> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var text: String?
    get() = if (attributes.containsKey("comment")) {
      attributes["comment"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["comment"] = value
    }

  override var spoiler: Boolean
    get() = if (attributes.containsKey("spoiler")) {
      attributes["spoiler"] as Boolean
    } else {
      super.spoiler
    }
    set(`value`) {
      attributes["spoiler"] = value
    }

  override var commentableType: String
    get() = if (attributes.containsKey("commentable_type")) {
      attributes["commentable_type"] as String
    } else {
      throw NoSuchElementException("commentableType not found")
    }
    set(`value`) {
      attributes["commentable_type"] = value
    }

  override var commentableId: Int
    get() = if (attributes.containsKey("commentable_id")) {
      attributes["commentable_id"] as Int
    } else {
      throw NoSuchElementException("commentableId not found")
    }
    set(`value`) {
      attributes["commentable_id"] = value
    }

  override var userId: String?
    get() = if (attributes.containsKey("user_id")) {
      attributes["user_id"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["user_id"] = value
    }

  override var edited: Boolean
    get() = if (attributes.containsKey("edited")) {
      attributes["edited"] as Boolean
    } else {
      super.edited
    }
    set(`value`) {
      attributes["edited"] = value
    }

  override var repliesCount: Int
    get() = if (attributes.containsKey("replies_count")) {
      attributes["replies_count"] as Int
    } else {
      super.repliesCount
    }
    set(`value`) {
      attributes["replies_count"] = value
    }

  override var reactionsSummary: List<Map<String, Any?>>
    get() = if (attributes.containsKey("reactions_summary")) {
      attributes["reactions_summary"] as List<Map<String, Any?>>
    } else {
      super.reactionsSummary
    }
    set(`value`) {
      attributes["reactions_summary"] = value
    }

  override var myReaction: String?
    get() = if (attributes.containsKey("my_reaction")) {
      attributes["my_reaction"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["my_reaction"] = value
    }

  override var createdAt: String?
    get() = if (attributes.containsKey("created_at")) {
      attributes["created_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["created_at"] = value
    }

  override var updatedAt: String?
    get() = if (attributes.containsKey("updated_at")) {
      attributes["updated_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["updated_at"] = value
    }

  override var user: User?
    get() = if (relationships.containsKey("user") && (relationships["user"] == null || relationships["user"] is User?)) {
      relationships["user"] as User?
    } else {
      null
    }
    set(`value`) {
      relationships["user"] = value
    }

  public constructor(
    text: String? = null,
    spoiler: Boolean? = null,
    commentableType: String? = null,
    commentableId: Int? = null,
    userId: String? = null,
    edited: Boolean? = null,
    repliesCount: Int? = null,
    reactionsSummary: List<Map<String, Any?>>? = null,
    myReaction: String? = null,
    createdAt: String? = null,
    updatedAt: String? = null,
    user: User? = null,
  ) {
    if (text != null) {
      attributes["comment"] = text
    }
    if (spoiler != null) {
      attributes["spoiler"] = spoiler
    }
    if (commentableType != null) {
      attributes["commentable_type"] = commentableType
    }
    if (commentableId != null) {
      attributes["commentable_id"] = commentableId
    }
    if (userId != null) {
      attributes["user_id"] = userId
    }
    if (edited != null) {
      attributes["edited"] = edited
    }
    if (repliesCount != null) {
      attributes["replies_count"] = repliesCount
    }
    if (reactionsSummary != null) {
      attributes["reactions_summary"] = reactionsSummary
    }
    if (myReaction != null) {
      attributes["my_reaction"] = myReaction
    }
    if (createdAt != null) {
      attributes["created_at"] = createdAt
    }
    if (updatedAt != null) {
      attributes["updated_at"] = updatedAt
    }
    if (user != null) {
      relationships["user"] = user
    }
  }

  public companion object : Resource.CompanionObj<Comment> {
    override val resourceType: String = "Comment"

    override val endpoint: String = "comments"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> Comment = { Comment() }

    override val schema: KClass<CommentSchema> = CommentSchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<Comment>.() -> Unit = {}): Scope<Comment> = Scope(Comment::class, options)

    public final suspend fun all(): CollectionProxy<Comment> = scope().all()

    public final suspend fun find(id: String): RecordProxy<Comment> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<Comment?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<Comment> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<Comment?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<Comment> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<Comment?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<Comment> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<Comment> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<Comment> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<Comment> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<Comment> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<Comment> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<Comment> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<Comment> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<Comment> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<Comment> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<Comment>()
  }
}
